import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import PennText from "@/components/ui/text/PennText";
import { PennButton } from "@/components/ui/button/PennButton";
import { PennDropdown } from "@/components/ui";
import { useFastenerBrands } from "@/hooks/useFastenerBrands";
import { useMaxThreadSizes } from "@/hooks/useMaxThreadSizes";
import { useSelectorEval } from "@/hooks/useSelectorEval";
import { useRegion } from "@/hooks/useRegion";
import type {
  RegionCode,
  RequiredForce,
  SelectorEvalRequest,
  SelectorEvalResponse,
} from "@/api/filter";
import type { ApplicationSummary } from "@/api/applications";
import { getApiErrorMessage } from "@penn/web-common";
import { clearAllProductSelections } from "@/utils/configure-storage";
import {
  ANY_THREAD,
  buildMaxThreadSizeOptions,
} from "@/utils/max-thread-size-options";
import { PAGE_CONTENT_CLASS } from "@/constants/pageContent";

const EVAL_DEBOUNCE_MS = 250;
// Fallback installation-force slider bounds, used only until the
// catalog-driven bounds load. The live bounds are derived per region from
// product `max_force_kn` (POC `computeForceBoundsFromProducts`) so the slider
// auto-tracks the catalog instead of being pinned to a hardcoded ceiling.
const FORCE_MIN_FALLBACK = 0;
const FORCE_MAX_FALLBACK = 300;
const EMPTY_THREAD_SIZES: number[] = [];

export type FilterState = {
  fasteners: string[];
  threadSize: string;
  processMonitoring: boolean;
  robotIntegration: boolean;
  forceMin: number;
  forceMax: number;
};

export type FilterPanelStatus = {
  isPending: boolean;
  isError: boolean;
  errorMessage: string | null;
};

/**
 * D2 (POC parity) — a one-shot filter restoration from a task snapshot. The
 * parent bumps `token` (unique per "open task") and supplies the frozen filter
 * values; FilterPanel applies them to its internal state exactly once per token
 * so the chips match `applyTaskSnapshot` without
 * becoming a fully-controlled component. Omitted fields keep their current
 * value; `forceMin/Max` are still clamped to the live catalog bounds.
 */
export type FilterSeed = {
  token: string;
  fasteners?: string[];
  threadSize?: string;
  processMonitoring?: boolean;
  robotIntegration?: boolean;
  forceMin?: number;
  forceMax?: number;
};

type FilterPanelProps = {
  matchCount?: number;
  onFilterChange?: (f: FilterState) => void;
  onResults?: (resp: SelectorEvalResponse | undefined) => void;
  onStatus?: (s: FilterPanelStatus) => void;
  onExistingApp?: () => void;
  onSetupNewApplication?: () => void;
  onResetAppDriven?: () => void;
  onResetSelection?: () => void;
  selectedApps?: ApplicationSummary[];
  appDrivenBrands?: string[];
  appDrivenIds?: string[];
  /** POC `Compare selected: N` pill. */
  compareSelectedCount?: number;
  /** D2: one-shot filter restoration from a task snapshot (applied once per token). */
  seed?: FilterSeed | null;
};

const INITIAL_FILTERS: FilterState = {
  fasteners: [],
  threadSize: ANY_THREAD,
  processMonitoring: false,
  robotIntegration: false,
  forceMin: FORCE_MIN_FALLBACK,
  forceMax: FORCE_MAX_FALLBACK,
};

function buildEvalBody(
  region: RegionCode,
  filters: FilterState,
  appDrivenBrands: string[] | undefined,
  appDrivenIds: string[] | undefined,
  forceMinBound: number,
  forceMaxBound: number,
): SelectorEvalRequest {
  const body: SelectorEvalRequest = { region };

  const appBrands = appDrivenBrands ?? [];
  const manualBrands = filters.fasteners;
  const brands = appBrands.length > 0 ? appBrands : manualBrands;
  if (brands.length === 1) body.fastenerBrand = brands[0];
  else if (brands.length > 1) body.fastenerBrand = brands;

  if (filters.threadSize !== ANY_THREAD) {
    const n = Number(filters.threadSize);
    if (Number.isFinite(n) && n > 0) body.maxThreadSize = n;
  }
  if (filters.processMonitoring) body.requireProcessMonitoring = true;
  if (filters.robotIntegration) body.requireRobotIntegration = true;
  if (filters.forceMin > forceMinBound) body.minForceKn = filters.forceMin;
  if (filters.forceMax < forceMaxBound) body.maxForceKn = filters.forceMax;
  if (appDrivenIds && appDrivenIds.length > 0) {
    body.applicationIds = appDrivenIds.slice(0, 50);
  }
  return body;
}

// The required force depends only on the selected applications.
const requirementKey = (body: SelectorEvalRequest) =>
  (body.applicationIds ?? []).join(",");

export const FilterPanel = ({
  matchCount,
  onFilterChange,
  onResults,
  onStatus,
  onExistingApp,
  onSetupNewApplication,
  onResetAppDriven,
  onResetSelection,
  selectedApps,
  appDrivenBrands,
  appDrivenIds,
  compareSelectedCount = 0,
  seed,
}: FilterPanelProps) => {
  const region = useRegion();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);

  // D2 — apply a task snapshot's frozen filters once per `seed.token`. Runs
  // after the force-bounds clamp effect's normal flow; clamping keeps seeded
  // force values inside the live catalog bounds.
  const appliedSeedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!seed || appliedSeedRef.current === seed.token) return;
    appliedSeedRef.current = seed.token;
    setFilters((f) => ({
      ...f,
      ...(seed.fasteners !== undefined ? { fasteners: seed.fasteners } : {}),
      ...(seed.threadSize !== undefined ? { threadSize: seed.threadSize } : {}),
      ...(seed.processMonitoring !== undefined
        ? { processMonitoring: seed.processMonitoring }
        : {}),
      ...(seed.robotIntegration !== undefined
        ? { robotIntegration: seed.robotIntegration }
        : {}),
      ...(seed.forceMin !== undefined ? { forceMin: seed.forceMin } : {}),
      ...(seed.forceMax !== undefined ? { forceMax: seed.forceMax } : {}),
    }));
  }, [seed]);

  // POC parity (resetAppSelectionAndFilters): when the
  // application selection is emptied (Option B cleared / last app removed), drop
  // the manual Option A fastener chips. Otherwise a fastener selection made
  // BEFORE picking an application — overridden while the app is selected — would
  // silently resurrect once the app is removed. Only acts on the
  // non-empty -> empty transition, so manual-only usage is untouched.
  const hadAppsRef = useRef((selectedApps?.length ?? 0) > 0);
  useEffect(() => {
    const hasApps = (selectedApps?.length ?? 0) > 0;
    const had = hadAppsRef.current;
    hadAppsRef.current = hasApps;
    if (had && !hasApps && filters.fasteners.length > 0) {
      const next = { ...filters, fasteners: [] };
      setFilters(next);
      onFilterChange?.(next);
    }
  }, [selectedApps, filters, onFilterChange]);

  const { data: fastenerBrands = [] } = useFastenerBrands();
  const effectiveBrands =
    (appDrivenBrands?.length ?? 0) > 0 ? appDrivenBrands! : filters.fasteners;
  const activeBrand =
    effectiveBrands.length === 1 ? effectiveBrands[0] : undefined;
  // Keep `Any` available while this query is loading or if it fails. A single
  // active brand narrows the DB-backed global list; multi-brand mode uses the
  // complete cross-region set because the lookup endpoint accepts one brand.
  const threadSizesQuery = useMaxThreadSizes(activeBrand);
  const threadSizes = threadSizesQuery.data ?? EMPTY_THREAD_SIZES;

  // A brand change can make a previously selected size invalid. Wait for the
  // new lookup to succeed before clearing it so a transient loading or error
  // state cannot erase the user's choice.
  useEffect(() => {
    if (!threadSizesQuery.isSuccess || filters.threadSize === ANY_THREAD) return;
    const selectedSize = Number(filters.threadSize);
    if (Number.isInteger(selectedSize) && threadSizes.includes(selectedSize)) {
      return;
    }
    const next = { ...filters, threadSize: ANY_THREAD };
    setFilters(next);
    onFilterChange?.(next);
  }, [filters, onFilterChange, threadSizes, threadSizesQuery.isSuccess]);

  // Derive the installation-force slider bounds from the catalog instead of
  // hardcoding 21–300. A region-only eval returns the full product set for the
  // active region; the bounds are floor(min)/ceil(max) of their `max_force_kn`
  // (POC `computeForceBoundsFromProducts`). The body is `{ region }` only, so
  // this query is independent of the user's filters — the bounds stay stable
  // while they narrow the slider, and grow automatically if a higher-force
  // product is added to the catalog.
  const boundsBody = useMemo<SelectorEvalRequest>(() => ({ region }), [region]);
  const boundsQuery = useSelectorEval(boundsBody);
  const { forceMinBound, forceMaxBound } = useMemo(() => {
    // Only products with a REAL installation-force rating define the slider
    // bounds. Unrated products come back as `null` (most) or `0` (e.g. MDS A100);
    // both must be excluded or the lower bound collapses to 0. POC data has no
    // zeros, so `computeForceBoundsFromProducts` yields 21 — requiring `v > 0`
    // reproduces that POC floor instead of letting a 0-rated product drag it down.
    const vals = (boundsQuery.data?.products ?? [])
      .map((p) => p.maxForceKn)
      .filter(
        (v): v is number => typeof v === "number" && Number.isFinite(v) && v > 0,
      );
    if (vals.length === 0) {
      return { forceMinBound: FORCE_MIN_FALLBACK, forceMaxBound: FORCE_MAX_FALLBACK };
    }
    return {
      forceMinBound: Math.floor(Math.min(...vals)),
      forceMaxBound: Math.ceil(Math.max(...vals)),
    };
  }, [boundsQuery.data]);

  // Keep the slider selection coherent with the live bounds. When the bounds
  // change (region switch / first load) a full-range selection snaps to the new
  // bounds so it still reads "Any"; a narrowed selection is clamped into range.
  // The ref guard means this only fires when the bounds actually change.
  const forceBoundsRef = useRef({ min: FORCE_MIN_FALLBACK, max: FORCE_MAX_FALLBACK });
  useEffect(() => {
    const prev = forceBoundsRef.current;
    if (prev.min === forceMinBound && prev.max === forceMaxBound) return;
    forceBoundsRef.current = { min: forceMinBound, max: forceMaxBound };
    setFilters((f) => {
      const wasFullRange = f.forceMin <= prev.min && f.forceMax >= prev.max;
      if (wasFullRange) {
        return { ...f, forceMin: forceMinBound, forceMax: forceMaxBound };
      }
      const lo = Math.min(Math.max(f.forceMin, forceMinBound), forceMaxBound);
      const hi = Math.max(Math.min(f.forceMax, forceMaxBound), forceMinBound);
      return { ...f, forceMin: lo, forceMax: hi };
    });
  }, [forceMinBound, forceMaxBound]);

  const evalBody = useMemo(
    () =>
      buildEvalBody(
        region,
        filters,
        appDrivenBrands,
        appDrivenIds,
        forceMinBound,
        forceMaxBound,
      ),
    [region, filters, appDrivenBrands, appDrivenIds, forceMinBound, forceMaxBound],
  );

  const [debouncedBody, setDebouncedBody] = useState(evalBody);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedBody(evalBody), EVAL_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [evalBody]);

  const evalQuery = useSelectorEval(debouncedBody);
  const evalData = evalQuery.data;
  useEffect(() => {
    onResults?.(evalData);
  }, [evalData, onResults]);
  useEffect(() => {
    onStatus?.({
      isPending: evalQuery.isPending || evalQuery.isFetching,
      isError: evalQuery.isError,
      errorMessage: evalQuery.error
        ? getApiErrorMessage(evalQuery.error, "Failed to load products.")
        : null,
    });
  }, [evalQuery.isPending, evalQuery.isFetching, evalQuery.isError, evalQuery.error, onStatus]);

  const threadSizeOptions = useMemo(
    () => buildMaxThreadSizeOptions(threadSizes),
    [threadSizes],
  );

  // A kept-previous response may answer an older application selection.
  const [settledRequirement, setSettledRequirement] = useState<{
    key: string;
    force: RequiredForce | null;
  } | null>(null);
  useEffect(() => {
    if (!evalData || evalQuery.isPlaceholderData) return;
    const key = requirementKey(debouncedBody);
    const force = evalData.requiredForce;
    setSettledRequirement((prev) =>
      prev?.key === key && prev.force?.forceLbs === force?.forceLbs
        ? prev
        : { key, force },
    );
  }, [evalData, evalQuery.isPlaceholderData, debouncedBody]);

  // Lock the slider at the server-enforced force, rounded up so it never reads looser (MICS-55).
  const requiredForce =
    settledRequirement?.key === requirementKey(evalBody) ? settledRequirement.force : null;
  const forceLockMin = requiredForce
    ? Math.min(
        Math.max(forceMinBound, Math.ceil(requiredForce.forceKn * 100) / 100),
        forceMaxBound,
      )
    : forceMinBound;
  const forceMinShown = Math.max(filters.forceMin, forceLockMin);

  // A range the requirement makes incompatible resets to the compatible one.
  useEffect(() => {
    const minBelowLock =
      filters.forceMin > forceMinBound && filters.forceMin < forceLockMin;
    const maxBelowLock = filters.forceMax < forceLockMin;
    if (!minBelowLock && !maxBelowLock) return;
    const next = {
      ...filters,
      ...(minBelowLock ? { forceMin: forceMinBound } : {}),
      ...(maxBelowLock ? { forceMax: forceMaxBound } : {}),
    };
    setFilters(next);
    onFilterChange?.(next);
  }, [filters, forceLockMin, forceMinBound, forceMaxBound, onFilterChange]);

  const resolvedMatchCount = matchCount ?? evalData?.products.length ?? 0;
  const optionBLocked = (appDrivenBrands?.length ?? 0) > 0;

  const update = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    onFilterChange?.(next);
  };

  const toggleFastener = (f: string) => {
    if (optionBLocked) return;
    const next = filters.fasteners.includes(f)
      ? filters.fasteners.filter((x) => x !== f)
      : [...filters.fasteners, f];
    update("fasteners", next);
  };

  // At the lock the bound is stored, so only a user's stricter minimum is sent.
  const setForceMin = (raw: number) => {
    const v = Number.isFinite(raw) ? raw : forceMinBound;
    const clamped = Math.max(forceLockMin, Math.min(v, filters.forceMax));
    update("forceMin", clamped <= forceLockMin ? forceMinBound : clamped);
  };
  const setForceMax = (raw: number) => {
    const v = Number.isFinite(raw) ? raw : forceMaxBound;
    update("forceMax", Math.min(forceMaxBound, Math.max(v, forceMinShown)));
  };
  const resetForce = () => {
    const next = { ...filters, forceMin: forceMinBound, forceMax: forceMaxBound };
    setFilters(next);
    onFilterChange?.(next);
  };
  const resetAll = () => {
    const resetFilters: FilterState = {
      ...INITIAL_FILTERS,
      forceMin: forceMinBound,
      forceMax: forceMaxBound,
    };
    setFilters(resetFilters);
    onFilterChange?.(resetFilters);
    onResetAppDriven?.();
    onResetSelection?.();
    // Reset drops the machine selection, so the saved Configure selections
    // behind it are stale — the next Configure must render the options the API
    // recommends for the reset scope.
    clearAllProductSelections();
    // Always re-run the evaluation, even when the filters were already at their
    // defaults. Skip the debounce so the request fires immediately, and
    // invalidate the cache so react-query can't serve a stale/cached result and
    // make the Reset button look like it did nothing.
    const resetBody = buildEvalBody(
      region,
      resetFilters,
      [],
      [],
      forceMinBound,
      forceMaxBound,
    );
    setDebouncedBody(resetBody);
    void queryClient.invalidateQueries({ queryKey: ["selector", "eval"] });
  };
  const forceLabel =
    forceMinShown === forceMinBound && filters.forceMax === forceMaxBound
      ? "Any"
      : `${forceMinShown}–${filters.forceMax} kN`;
  const forceSpan = Math.max(1, forceMaxBound - forceMinBound);
  const forceLeftPct = ((forceMinShown - forceMinBound) / forceSpan) * 100;
  const forceRightPct = ((filters.forceMax - forceMinBound) / forceSpan) * 100;

  const brandsSummary = optionBLocked
    ? `Auto from app: ${(appDrivenBrands ?? []).join(", ")}`
    : filters.fasteners.length > 0
      ? filters.fasteners.join(", ")
      : "none selected";

  const threadLabel = filters.threadSize === ANY_THREAD ? "Any" : `M${filters.threadSize}`;
  const mustHaves = [
    filters.processMonitoring ? "Process monitoring" : null,
    filters.robotIntegration ? "Robot integration" : null,
  ]
    .filter(Boolean)
    .join(", ");

  const appNames = (selectedApps ?? []).map(
    (a) => a.name ?? a.applicationNumber ?? a.applicationId,
  );
  const appSelectionSummary =
    appNames.length === 0
      ? "No application selected."
      : `Selected: ${appNames.slice(0, 2).join(", ")}${
          appNames.length > 2 ? ` (+${appNames.length - 2} more)` : ""
        }`;

  return (
    <div>
      <div className={`${PAGE_CONTENT_CLASS} py-5`}>
      <div className="relative mb-4">
        <div className="mb-3 sm:mb-0 sm:absolute sm:left-0 sm:top-0">
          <Link
            to="/"
            className="text-button text-penn-100 hover:text-white hover:underline"
          >
            ← Back Home
          </Link>
        </div>
        <div className="text-center">
          <PennText as="h2" size="2xl" color="on-navy" font="semibold" align="center">
            Filter functions
          </PennText>
          <PennText as="p" size="base" color="on-navy-secondary" align="center" className="mt-1">
            Use these filters to narrow down the solution list.
          </PennText>
        </div>
        <div className="mt-3 flex justify-center sm:mt-0 sm:absolute sm:right-0 sm:top-0">
          <button
            type="button"
            onClick={resetAll}
            className="inline-flex items-center gap-2 rounded-lg border border-penn-500 bg-white px-4 py-2 text-xs font-semibold text-penn-700 shadow-sm transition-colors hover:bg-penn-100 active:bg-penn-100 cursor-pointer"
          >
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
              <path d="M3 3v5h5" />
            </svg>
            Reset filters
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 items-stretch">
        {/* Option A — heading lives OUTSIDE the card (POC parity) */}
        <div className="lg:col-span-7 flex flex-col gap-2">
          <div className="px-1">
            <p className="text-label uppercase text-on-navy-secondary">Option A</p>
            <p className="mt-1 text-h4 text-on-navy">Set Filters Manually</p>
          </div>
          <div className="h-full rounded-2xl border border-gray-200 bg-white p-4 shadow-soft">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 items-start">
            <div className="lg:col-span-6">
              <FilterLabel>1) Fastener Brand</FilterLabel>
              <div className="flex flex-wrap gap-2 mt-2 ">
                {fastenerBrands?.map((brand) => {
                  const active = optionBLocked
                    ? (appDrivenBrands ?? []).includes(brand.code)
                    : filters.fasteners.includes(brand.code);
                  return (
                    <button
                      key={brand.code}
                      type="button"
                      disabled={optionBLocked}
                      onClick={() => toggleFastener(brand.code)}
                      className={`whitespace-nowrap px-3 py-2 rounded-xl text-sm font-medium border shadow-sm transition-colors
                        ${active
                          ? "bg-penn-500 text-white border-penn-500"
                          : optionBLocked
                            ? "border-gray-100 text-gray-300 bg-gray-50 cursor-not-allowed"
                            : "border-gray-300 text-blue_grey1 bg-white hover:bg-gray-50 cursor-pointer"
                        }`}
                      title={
                        optionBLocked
                          ? "Brands are set by selected applications"
                          : undefined
                      }
                    >
                      {brand.displayName}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="lg:col-span-3">
              <FilterLabel>2) Maximum thread size</FilterLabel>
              <div className="mt-2">
                <PennDropdown
                  ariaLabel="Maximum thread size"
                  options={threadSizeOptions}
                  value={filters.threadSize}
                  onChange={(v) => update("threadSize", v)}
                />
              </div>
            </div>

            <div className="lg:col-span-3">
              <FilterLabel>3) Must-have capabilities</FilterLabel>
              <div className="mt-2 space-y-1.5">
                <CheckboxRow
                  label="Process monitoring"
                  checked={filters.processMonitoring}
                  onChange={(v) => update("processMonitoring", v)}
                />
                <CheckboxRow
                  label="Robot integration"
                  checked={filters.robotIntegration}
                  onChange={(v) => update("robotIntegration", v)}
                />
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50/40 px-4 py-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
              <div className="flex items-center justify-between lg:justify-start gap-3 min-w-60">
                <FilterLabel>4) Installation force range (kN)</FilterLabel>
                <div className="flex items-center gap-2">
                  <PennText as="span" size="xs" color="blue_grey">{forceLabel}</PennText>
                  <button
                    type="button"
                    onClick={resetForce}
                    className="text-xs text-penn-700 hover:underline cursor-pointer"
                  >
                    Reset
                  </button>
                </div>
              </div>

              <div className="relative flex-1 h-6">
                <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1 rounded-full bg-gray-200" />
                <div
                  className="absolute top-1/2 -translate-y-1/2 h-1 rounded-full bg-penn-500"
                  style={{
                    left: `${forceLeftPct}%`,
                    width: `${Math.max(0, forceRightPct - forceLeftPct)}%`,
                  }}
                />
                <input
                  type="range"
                  aria-label="Minimum installation force"
                  min={forceMinBound}
                  max={forceMaxBound}
                  value={forceMinShown}
                  onChange={(e) => setForceMin(Number(e.target.value))}
                  className="dualRange"
                />
                <input
                  type="range"
                  aria-label="Maximum installation force"
                  min={forceMinBound}
                  max={forceMaxBound}
                  value={filters.forceMax}
                  onChange={(e) => setForceMax(Number(e.target.value))}
                  className="dualRange"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="number"
                  aria-label="Minimum (kN)"
                  min={forceMinBound}
                  max={forceMaxBound}
                  value={forceMinShown}
                  onChange={(e) => setForceMin(Number(e.target.value))}
                  placeholder="Min"
                  className="w-24 h-9 rounded-xl border border-gray-300 px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-penn-300"
                />
                <PennText as="span" size="xs" color="blue_grey">to</PennText>
                <input
                  type="number"
                  aria-label="Maximum (kN)"
                  min={forceMinBound}
                  max={forceMaxBound}
                  value={filters.forceMax}
                  onChange={(e) => setForceMax(Number(e.target.value))}
                  placeholder="Max"
                  className="w-24 h-9 rounded-xl border border-gray-300 px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-penn-300"
                />
              </div>
            </div>
          </div>

          {/* Result summary + filter tags — INSIDE the card (POC parity) */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-penn-900">{resolvedMatchCount}</span>
            <span className="text-sm text-blue_grey">matching product(s)</span>
            <Pill>Region: {region}</Pill>
            <Pill>Fastener Brand: {brandsSummary}</Pill>
            <Pill>Max thread: {threadLabel}</Pill>
            <Pill>Force: {forceLabel}</Pill>
            {mustHaves && <Pill>Must-haves: {mustHaves}</Pill>}
            <Pill>Compare selected: {compareSelectedCount}</Pill>
          </div>
          </div>
        </div>

        {/* Option B — heading lives OUTSIDE the card (POC parity) */}
        <div className="lg:col-span-5 flex flex-col gap-2">
          <div className="px-1">
            <p className="text-label uppercase text-on-navy-secondary">Option B</p>
            <p className="mt-1 text-h4 text-on-navy">Automatic Setting of Filters Based on Application</p>
          </div>
          <div className="h-full rounded-2xl border border-gray-200 bg-white p-4 shadow-soft">
            <p className="text-sm font-semibold text-blue_grey1">Application details</p>
            <p className="mt-1 text-xs text-blue_grey">Create or select an application to auto-set filters.</p>

            <div className="mt-3 grid grid-cols-1 gap-2">
              <PennButton
                variant="primary"
                size="sm"
                fullWidth
                onClick={onSetupNewApplication}
                disabled={!onSetupNewApplication}
              >
                Setup New Application
              </PennButton>
              <PennButton variant="secondary" size="sm" fullWidth onClick={onExistingApp}>
                Use Existing Application
              </PennButton>
            </div>

            {/* Selection summary + reset live BELOW the buttons (POC parity) */}
            <p className="mt-3 text-xs text-blue_grey">{appSelectionSummary}</p>
            {(selectedApps?.length ?? 0) > 0 && (
              <button
                type="button"
                onClick={onResetAppDriven}
                className="mt-3 h-9 rounded-xl border border-gray-300 bg-white px-3 text-xs font-semibold text-blue_grey1 shadow-sm transition-colors hover:bg-gray-50 cursor-pointer"
              >
                Reset application
              </button>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

const FilterLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm font-semibold text-blue_grey1">{children}</p>
);

const Pill = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-blue_grey1">
    {children}
  </span>
);

const CheckboxRow = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) => (
  <label className="flex items-center gap-2 cursor-pointer group">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 accent-penn-500 cursor-pointer"
    />
    <PennText as="span" size="sm" color="blue_grey" className="group-hover:text-blue_grey1 transition-colors">
      {label}
    </PennText>
  </label>
);
